import { Box3, Vector3 } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import type { StudioAssetBounds, StudioAssetRecord } from '../../engine';
import { apiPost, apiUpload } from '../services/api';

export const MAX_GLB_BYTES = 10 * 1024 * 1024;
export const MAX_GLB_MIB = MAX_GLB_BYTES / 1024 / 1024;

/** Neutral 1m fallback when bounds can't be extracted from a malformed model. */
const FALLBACK_BOUNDS: StudioAssetBounds = { size: [1, 1, 1], center: [0, 0, 0] };

export class AssetValidationError extends Error {}

function validate(file: File): void {
  if (!/\.glb$/i.test(file.name)) {
    throw new AssetValidationError('Only .glb models are supported.');
  }
  if (file.size > MAX_GLB_BYTES) {
    throw new AssetValidationError(
      `Model is ${(file.size / 1024 / 1024).toFixed(1)} MiB; the limit is ${MAX_GLB_MIB} MiB.`,
    );
  }
}

/** Parse a GLB ArrayBuffer and return its axis-aligned bounds (gracefully). */
export async function parseGlbBounds(buffer: ArrayBuffer): Promise<StudioAssetBounds> {
  const loader = new GLTFLoader();
  try {
    const gltf = await new Promise<{ scene: import('three').Object3D }>((resolve, reject) => {
      loader.parse(buffer, '', resolve, reject);
    });
    const box = new Box3().setFromObject(gltf.scene);
    if (box.isEmpty()) return FALLBACK_BOUNDS;
    const size = new Vector3();
    const center = new Vector3();
    box.getSize(size);
    box.getCenter(center);
    return {
      size: [round(size.x), round(size.y), round(size.z)],
      center: [round(center.x), round(center.y), round(center.z)],
    };
  } catch {
    return FALLBACK_BOUNDS;
  }
}

function round(n: number): number {
  return Number(n.toFixed(3));
}

interface PresignWire {
  upload_url: string;
  asset_id: string;
  s3_key: string;
  cf_url: string;
}

interface ConfirmedAssetWire {
  id: string;
  name: string;
  cf_url: string;
  file_size: number;
  created_at: string;
}

/**
 * Validate a .glb, extract its bounds, request a presigned URL, upload to S3,
 * confirm the asset, and return a StudioAssetRecord.
 */
export async function uploadGlbAsset(projectId: string, file: File): Promise<StudioAssetRecord> {
  validate(file);

  const buffer = await file.arrayBuffer();
  const bounds = await parseGlbBounds(buffer);

  // 1. Request presigned PUT URL
  const presignData = await apiPost<PresignWire>(`/projects/${projectId}/assets/presign`, {
    name: file.name,
    file_size: file.size,
    content_type: 'model/gltf-binary',
  });

  // 2. Upload file to S3
  await apiUpload(presignData.upload_url, file, 'model/gltf-binary');

  // 3. Confirm asset upload with API. Bounds ride in the asset meta so the
  // editor can register the model as a placeable object on later loads
  // without re-parsing the file.
  const confirmedAsset = await apiPost<ConfirmedAssetWire>(`/projects/${projectId}/assets/confirm`, {
    asset_id: presignData.asset_id,
    name: file.name.replace(/\.glb$/i, ''),
    file_size: file.size,
    content_type: 'model/gltf-binary',
    s3_key: presignData.s3_key,
    cf_url: presignData.cf_url,
    meta: { bounds },
  });

  return {
    id: confirmedAsset.id,
    name: confirmedAsset.name,
    kind: 'glb',
    storagePath: presignData.s3_key,
    downloadUrl: confirmedAsset.cf_url,
    bounds,
    category: 'structural',
    sizeBytes: confirmedAsset.file_size,
    createdAt: new Date(confirmedAsset.created_at).getTime(),
  };
}
